import { Injectable } from '@angular/core';
import { User } from "../../model/user";
import { RestService, LoginResponse, SupabaseTokenSet } from "../rest/rest.service";
import { Router, RouteReuseStrategy } from '@angular/router';
import {timer} from "rxjs";
import { LoggerService } from '../utils/logger.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { RevenueCatService } from '../billing/revenuecat.service';
import { isCapacitorNative } from '../platform/capacitor-platform';
import { AuthProvider, PersistedTokens, TokenStorageService } from './token-storage.service';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { LocalEntitlementService } from '../entitlement/local-entitlement.service';
import { BRAND } from '../brand/brand';

const counterSession = timer(0, 30000); // 30 secs
const counterUserActive = timer(0, 3000); // 3 secs

/** Refresh the access token this far before its real expiry to absorb clock skew + network jitter. */
const REFRESH_LEEWAY_MS = 60_000;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  static userKey: string = "user";
  timeSubscription: any;
  timeSubscriptionUserActive: any;

  private inFlightRefresh: Promise<boolean> | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private cachedTokens: PersistedTokens | null = null;

  constructor(
    private restService: RestService,
    private router: Router,
    private routeReuseStrategy: RouteReuseStrategy,
    private oauthService: OAuthService,
    private revenueCat: RevenueCatService,
    private tokenStorage: TokenStorageService,
    private connectivity: ConnectivityService,
    private localEntitlements: LocalEntitlementService,
  ) {
    this.connectivity.connected$.subscribe((online) => {
      if (!online || !this.isAuthenticated()) return;
      this.lastSessionCheck = null;
      void this.restoreSessionFromStorage().then((restored) => {
        if (restored) void this.verifySessionWithBackend();
      });
    });
  }

  private clearRouteCache(): void {
    const anyStrategy = this.routeReuseStrategy as any;
    if (anyStrategy && typeof anyStrategy.clear === 'function') {
      anyStrategy.clear();
    }
  }

  private startSessionMonitoring(): void {
    if (this.timeSubscription) {
      this.timeSubscription.unsubscribe();
      this.timeSubscription = null;
    }
    if (this.timeSubscriptionUserActive) {
      this.timeSubscriptionUserActive.unsubscribe();
      this.timeSubscriptionUserActive = null;
    }
    this.timeSubscription = counterSession.subscribe(() => {
      this.checkServerSession();
    });
    this.timeSubscriptionUserActive = counterUserActive.subscribe(() => {
      this.checkUserActive();
    });
  }

  /** Supabase email + password (requires publishable or anon API key + project URL in runtime-config). */
  async signInWithEmailPassword(email: string, password: string): Promise<boolean> {
    try {
      this.clearRouteCache();
      const tokens = await this.restService.supabasePasswordGrant(email, password);
      if (!tokens) {
        return false;
      }
      const profile = await this.restService.fetchAuthMe(tokens.accessToken);
      if (!profile?.sessionId) {
        return false;
      }
      await this.persistOidcLogin(profile, tokens.accessToken, {
        provider: 'supabase-password',
        refreshToken: tokens.refreshToken,
        expiresAt: tokens.expiresAt,
      });
      return true;
    } catch (error) {
      LoggerService.error('Email/password sign-in failed', error);
      return false;
    }
  }

  /** Emergency admin login (backend EMERGENCY_ADMIN_ON). */
  async authenticate(username: string, password: string): Promise<boolean> {
    try {
      this.clearRouteCache();
      const loginResponse = await this.restService.login(username, password);

      if (loginResponse && loginResponse.sessionId) {
        const adminRaw = (loginResponse as any).admin;
        const admin =
          adminRaw === true ||
          adminRaw === 1 ||
          adminRaw === '1' ||
          adminRaw === 'true';
        const name = loginResponse.username ?? username;
        const email = loginResponse.email ?? username;
        const user = new User(loginResponse.userId, name, email, loginResponse.sessionId, true, admin);
        const stored = {
          _id: user.id,
          _name: user.name,
          _email: user.email,
          _sid: user.sid,
          _isActive: user.isActive,
          _admin: user.admin
        };
        if (isCapacitorNative() && admin) {
          LoggerService.log('Emergency admin login blocked on mobile');
          return false;
        }

        localStorage.setItem(AuthService.userKey, JSON.stringify(stored));
        this.lastSessionCheck = { valid: true, at: Date.now() };
        LoggerService.log('User authenticated (emergency)', { userId: loginResponse.userId, admin });

        this.clearRouteCache();
        this.startSessionMonitoring();
        this.identifyBillingUser();

        return true;
      } else {
        LoggerService.log('Failed to retrieve session ID');
        return false;
      }
    } catch (error) {
      LoggerService.error('Login failed', error);
      return false;
    }
  }

  /**
   * After OIDC / Supabase login: persist profile + access token + optional refresh token.
   *
   * `tokenSet` carries the long-lived refresh material that drives "stay signed in".
   * For OAuth callbacks where the refresh token lives inside `angular-oauth2-oidc`,
   * callers may pass `null` and rely on that library to refresh.
   */
  async persistOidcLogin(
    profile: LoginResponse,
    accessToken: string,
    tokenSet?: { provider: AuthProvider; refreshToken: string | null; expiresAt: number | null } | null,
  ): Promise<void> {
    this.clearRouteCache();
    const previousUserId = this.getUser()?.id;
    const adminRaw = (profile as any).admin;
    const admin =
      adminRaw === true ||
      adminRaw === 1 ||
      adminRaw === '1' ||
      adminRaw === 'true';
    const name = profile.username ?? profile.email ?? '';
    const email = profile.email ?? '';
    const user = new User(profile.userId, name, email, accessToken, true, admin);
    const stored = {
      _id: user.id,
      _name: user.name,
      _email: user.email,
      _sid: user.sid,
      _isActive: user.isActive,
      _admin: user.admin
    };
    localStorage.setItem(AuthService.userKey, JSON.stringify(stored));
    if (previousUserId && previousUserId !== user.id) {
      await this.localEntitlements.clear(previousUserId);
    }
    this.lastSessionCheck = { valid: true, at: Date.now() };
    if (tokenSet) {
      const persisted: PersistedTokens = {
        accessToken,
        refreshToken: tokenSet.refreshToken,
        expiresAt: tokenSet.expiresAt,
        provider: tokenSet.provider,
      };
      this.cachedTokens = persisted;
      await this.tokenStorage.save(persisted);
      this.scheduleProactiveRefresh(persisted);
    }
    LoggerService.log('User authenticated (OIDC)', { userId: profile.userId, admin });
    this.clearRouteCache();
    this.startSessionMonitoring();
    this.identifyBillingUser();
  }

  /**
   * Boot-time hook. Loads persisted tokens, refreshes the access JWT if it is
   * within the leeway of expiry, and keeps `localStorage.user._sid` in sync.
   *
   * Returns `true` when the app already has (or just obtained) a usable session.
   */
  async restoreSessionFromStorage(): Promise<boolean> {
    let tokens: PersistedTokens | null = null;
    try {
      tokens = await this.tokenStorage.load();
    } catch (err) {
      LoggerService.error('[auth] token storage load failed', err);
    }
    if (!tokens) return this.isAuthenticated();
    this.cachedTokens = tokens;

    // Reconcile the legacy `user._sid` blob with the persisted access token.
    const userJson = localStorage.getItem(AuthService.userKey);
    if (userJson) {
      try {
        const user = new User().fromJson(userJson);
        if (user.sid !== tokens.accessToken) {
          this.syncUserSid(tokens.accessToken);
        }
      } catch {
        /* stale blob - leave as is */
      }
    }

    if (this.isAccessTokenFresh(tokens)) {
      this.startSessionMonitoring();
      this.scheduleProactiveRefresh(tokens);
      return true;
    }
    // Offline study uses the locally verified entitlement lease. The access JWT
    // is refreshed as soon as connectivity returns.
    if (!this.connectivity.isOnline) {
      this.startSessionMonitoring();
      return this.isAuthenticated();
    }
    return this.refreshAccessTokenIfPossible();
  }

  /**
   * Refresh the access JWT using the stored Supabase refresh token. Coalesces
   * concurrent callers (HTTP 401 retry + scheduled timer) into one network call.
   */
  async refreshAccessTokenIfPossible(): Promise<boolean> {
    if (this.inFlightRefresh) return this.inFlightRefresh;
    this.inFlightRefresh = this.doRefresh().finally(() => {
      this.inFlightRefresh = null;
    });
    return this.inFlightRefresh;
  }

  private async doRefresh(): Promise<boolean> {
    const tokens = this.cachedTokens ?? (await this.tokenStorage.load());
    if (!tokens) return false;
    this.cachedTokens = tokens;
    if (!this.connectivity.isOnline) {
      return this.isAuthenticated();
    }

    if (tokens.provider !== 'supabase-password' && tokens.provider !== 'supabase-oauth') {
      // Emergency / dev sessions are server-side opaque tokens; nothing to refresh client-side.
      return this.isAccessTokenFresh(tokens);
    }
    if (!tokens.refreshToken) {
      LoggerService.log('[auth] No refresh token stored; cannot extend session.');
      return false;
    }

    const refreshed = await this.restService.supabaseRefreshGrant(tokens.refreshToken);
    if (!refreshed) {
      LoggerService.log('[auth] Refresh rejected by Supabase; clearing session.');
      await this.clearPersistedTokens();
      return false;
    }

    const next: PersistedTokens = {
      accessToken: refreshed.accessToken,
      refreshToken: refreshed.refreshToken ?? tokens.refreshToken,
      expiresAt: refreshed.expiresAt,
      provider: tokens.provider,
    };
    this.cachedTokens = next;
    await this.tokenStorage.save(next);
    this.syncUserSid(next.accessToken);
    this.lastSessionCheck = { valid: true, at: Date.now() };
    this.startSessionMonitoring();
    this.scheduleProactiveRefresh(next);
    this.identifyBillingUser();
    return true;
  }

  private identifyBillingUser(): void {
    if (!BRAND.showBilling) {
      return;
    }
    void this.revenueCat.ensureIdentified(this.getUser());
  }

  private isAccessTokenFresh(tokens: PersistedTokens): boolean {
    if (!tokens.accessToken) return false;
    if (tokens.expiresAt == null) return true; // emergency token or unknown expiry: trust until backend says otherwise
    return tokens.expiresAt - Date.now() > REFRESH_LEEWAY_MS;
  }

  private scheduleProactiveRefresh(tokens: PersistedTokens): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (tokens.expiresAt == null || !tokens.refreshToken) return;
    const delay = Math.max(15_000, tokens.expiresAt - Date.now() - REFRESH_LEEWAY_MS);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      if (this.connectivity.isOnline) {
        void this.refreshAccessTokenIfPossible();
      }
    }, delay);
  }

  private syncUserSid(accessToken: string): void {
    const userJson = localStorage.getItem(AuthService.userKey);
    if (!userJson) return;
    try {
      const parsed = JSON.parse(userJson) as Record<string, unknown>;
      parsed['_sid'] = accessToken;
      localStorage.setItem(AuthService.userKey, JSON.stringify(parsed));
    } catch {
      /* corrupt blob - ignore */
    }
  }

  private async clearPersistedTokens(): Promise<void> {
    this.cachedTokens = null;
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    await this.tokenStorage.clear();
  }

  private static isLikelyJwtAccessToken(sid: string | null | undefined): boolean {
    return !!sid && sid.split('.').length >= 3;
  }

  private async checkServerSession() {
    if (!this.connectivity.isOnline) return;

    let active:boolean = false;
    let user = null;
    const userJson = localStorage.getItem(AuthService.userKey);
    if (userJson != null) {
      user = new User().fromJson(userJson);
      active = await this.restService.checkSession(user?.sid);
    }

    if (!active) {
      // Try silent refresh before giving up — the access JWT may simply have expired.
      const refreshed = await this.refreshAccessTokenIfPossible();
      if (refreshed) {
        return;
      }
      localStorage.removeItem(AuthService.userKey);
      await this.clearPersistedTokens();
      this.revenueCat.closeIfConfigured();
      if (AuthService.isLikelyJwtAccessToken(user?.sid)) {
        try {
          this.oauthService.logOut(true);
        } catch {
          /* ignore */
        }
      }
      this.timeSubscription?.unsubscribe();
      this.timeSubscriptionUserActive?.unsubscribe();
      this.router.navigate(['/login']);
    }
  }

  /**
   * User not active after server errors
   */
  private async checkUserActive() {

    let active:boolean = false;
    let user = null;
    const userJson = localStorage.getItem(AuthService.userKey);
    if (userJson != null) {
      user = new User().fromJson(userJson);
      active = user.isActive;
    }

    if (!active) {
      localStorage.removeItem(AuthService.userKey);
      this.revenueCat.closeIfConfigured();
      if (AuthService.isLikelyJwtAccessToken(user?.sid)) {
        try {
          this.oauthService.logOut(true);
        } catch {
          /* ignore */
        }
      }
      this.timeSubscription?.unsubscribe();
      this.timeSubscriptionUserActive?.unsubscribe();
      this.router.navigate(['/login']);
    }
  }

  /** Cache for session verification (avoid calling API on every route change) */
  private lastSessionCheck: { valid: boolean; at: number } | null = null;
  private static readonly SESSION_CHECK_CACHE_MS = 60_000;

  isAuthenticated(): boolean {
    const userJson = localStorage.getItem(AuthService.userKey);
    if (userJson != null) {
      const user: User = new User().fromJson(userJson);
      return user.isActive;
    }
    return false;
  }

  /**
   * Verify session with backend. Caches result for SESSION_CHECK_CACHE_MS.
   *
   * When the backend says "invalid" we *do not* clear local state here anymore —
   * the caller (typically `AuthGuard`) gets the chance to call
   * {@link refreshAccessTokenIfPossible} first. Local state is only torn down by
   * `clearPersistedTokens()` / `logoutUser()` / the refresh handler itself.
   */
  async verifySessionWithBackend(): Promise<boolean> {
    const user = this.getUser();
    if (!user?.sid) return false;
    if (!this.connectivity.isOnline) return true;
    const now = Date.now();
    if (this.lastSessionCheck && (now - this.lastSessionCheck.at) < AuthService.SESSION_CHECK_CACHE_MS) {
      return this.lastSessionCheck.valid;
    }
    const valid = await this.restService.checkSession(user.sid);
    this.lastSessionCheck = { valid, at: now };
    return valid;
  }

  getUser() {
    if (this.isAuthenticated()) {
      const userJson = localStorage.getItem(AuthService.userKey);
      if (userJson != null) {
        return new User().fromJson(userJson);
      }
    }
    return null;
  }

  async invalidateLocalSession(): Promise<void> {
    const userId = this.getUser()?.id;
    localStorage.removeItem(AuthService.userKey);
    this.lastSessionCheck = null;
    await this.clearPersistedTokens();
    await this.localEntitlements.clear(userId);
    this.timeSubscription?.unsubscribe();
    this.timeSubscriptionUserActive?.unsubscribe();
    this.timeSubscription = null;
    this.timeSubscriptionUserActive = null;
  }

  async logoutUser(): Promise<void> {
    let user: User | null = null;
    const userJson = localStorage.getItem(AuthService.userKey);
    if (userJson != null) {
      user = new User().fromJson(userJson);
    }

    try {
      if (user != null && user.sid != null) {
        const jwt = AuthService.isLikelyJwtAccessToken(user.sid);
        await this.restService.logout(user.sid).catch((err) => {
          LoggerService.error('Logout request failed', err);
        });
        if (jwt) {
          await this.oauthService.revokeTokenAndLogout(true).catch(() => {
            try {
              this.oauthService.logOut(true);
            } catch {
              /* ignore */
            }
          });
        }
      }
    } finally {
      localStorage.removeItem(AuthService.userKey);
      await this.localEntitlements.clear(user?.id);
      this.lastSessionCheck = null;
      await this.clearPersistedTokens();
      this.clearRouteCache();
      this.revenueCat.closeIfConfigured();
      if (this.oauthService.hasValidAccessToken()) {
        try {
          this.oauthService.logOut(true);
        } catch {
          /* ignore */
        }
      }
      if (this.timeSubscription) {
        this.timeSubscription.unsubscribe();
        this.timeSubscription = null;
      }
      if (this.timeSubscriptionUserActive) {
        this.timeSubscriptionUserActive.unsubscribe();
        this.timeSubscriptionUserActive = null;
      }
      await this.delay(300);
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
